/* Bill Jewellers — HOME V3 "THE CASE" — boot + choreography (1 Aug 2026)
 *
 * Owns: Lenis + ScrollTrigger wiring (ONE rAF: gsap.ticker), the pinned
 * scroll-scrubbed film hero (James's locked-in blueprint: pin + scrub 0.5,
 * type sliding over the footage), reveals, ticker, live Dublin clock, the
 * mobile menu, every <video> lifecycle (the bj-media lesson: decoders are
 * rationed), the global velocity signal, and the tiered gate that imports
 * the WebGL world (js/bj-world.js).
 *
 * LAWS HONOURED HERE:
 * - Armed/hidden states exist only under html.bj3; this file confirms
 *   __bj3Live before the head failsafe fires, and the safety sweep SETS
 *   final values (never clears them) — the invisible-content bug class.
 * - iOS currentTime scrubbing is unreliable (keyframe-bound seeks), so
 *   touch devices play the loop and scroll drives the TYPE, not the frame.
 * - Reduced motion: this file does almost nothing (menu + clock only).
 */
(function () {
  'use strict';

  var REDUCED = matchMedia('(prefers-reduced-motion: reduce)').matches;
  var docEl = document.documentElement;

  /* ── Always-on basics (work under reduced motion + any tier) ────────── */

  // Live Dublin clock — a real value, computed, never claimed hours.
  (function clock() {
    var els = document.querySelectorAll('[data-c-clock]');
    if (!els.length) return;
    var fmt, tzFmt;
    try {
      fmt = new Intl.DateTimeFormat('en-IE', {
        hour: '2-digit', minute: '2-digit', hour12: false,
        timeZone: 'Europe/Dublin'
      });
      tzFmt = new Intl.DateTimeFormat('en-IE', {
        timeZoneName: 'short', timeZone: 'Europe/Dublin'
      });
    } catch (e) { return; }
    var tick = function () {
      var t = fmt.format(new Date());
      // Dublin is IST only in summer — derive the abbreviation, never
      // hardcode it (GMT half the year; a wrong zone is an invented fact).
      var tz = '';
      try {
        var part = tzFmt.formatToParts(new Date()).find(function (p) { return p.type === 'timeZoneName'; });
        tz = part ? ' ' + part.value : '';
      } catch (e) {}
      els.forEach(function (el) { el.textContent = t + tz; });
    };
    tick();
    setInterval(tick, 30000);
  })();

  // Mobile menu — inert/aria contract from the audited v2 sheet.
  (function menu() {
    var burger = document.querySelector('.c-masthead__burger');
    var menu = document.getElementById('c-menu');
    if (!burger || !menu) return;
    var close = menu.querySelector('.c-menu__close');
    var setOpen = function (open) {
      document.body.classList.toggle('c-nav-open', open);
      menu.classList.toggle('is-open', open);
      burger.setAttribute('aria-expanded', open ? 'true' : 'false');
      menu.setAttribute('aria-hidden', open ? 'false' : 'true');
      // The page behind the sheet goes inert (focus can't tab out of the
      // modal) and Lenis is stopped so it can't scroll underneath.
      ['main', '.c-footer', '.c-ticker'].forEach(function (sel) {
        var el = document.querySelector(sel);
        if (el) { if (open) el.setAttribute('inert', ''); else el.removeAttribute('inert'); }
      });
      if (window.__bj3Lenis) { open ? window.__bj3Lenis.stop() : window.__bj3Lenis.start(); }
      if (open) { menu.removeAttribute('inert'); menu.querySelector('a,button').focus(); }
      else { menu.setAttribute('inert', ''); burger.focus(); }
    };
    burger.addEventListener('click', function () { setOpen(!menu.classList.contains('is-open')); });
    if (close) close.addEventListener('click', function () { setOpen(false); });
    menu.addEventListener('click', function (e) {
      if (e.target.closest('a')) setOpen(false);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && menu.classList.contains('is-open')) setOpen(false);
    });
  })();

  /* ── Video lifecycle (all tiers except reduced motion) ──────────────── */
  var IS_TOUCH = matchMedia('(pointer: coarse)').matches;
  var videos = [];

  function armVideo(v) {
    // Sources are parked in data attributes at build time so the parser
    // never races us into seven simultaneous downloads (bj-media law).
    // Posters are parked the same way on below-fold films — a poster
    // attribute downloads EAGERLY regardless of preload="none", which was
    // ~600KB of off-screen fetches on a phone.
    var src = (IS_TOUCH && v.dataset.cSrcM) ? v.dataset.cSrcM : v.dataset.cSrc;
    if (!src || v.dataset.cArmed) return;
    v.dataset.cArmed = '1';
    if (v.dataset.cPoster && !v.poster) v.poster = v.dataset.cPoster;
    v.src = src;
    v.load();
  }

  function playVideo(v) {
    armVideo(v);
    var p = v.play();
    if (p && p.catch) p.catch(function () { /* Low Power Mode — poster stands */ });
  }

  if (REDUCED) {
    /* Reduced motion must still leave a COMPLETE page. Posters are parked in
       data-c-poster (they download eagerly otherwise, even under preload
       "none"), and armVideo is the only thing that restores them — so with
       motion off, four films (two rooms + two triptych panels) kept their
       full layout box with no src and no poster: four black holes beside the
       copy. Give them their still and load nothing else. */
    Array.prototype.forEach.call(
      document.querySelectorAll('video[data-c-poster]'),
      function (v) { if (!v.poster) v.poster = v.dataset.cPoster; }
    );
  } else {
    videos = Array.prototype.slice.call(document.querySelectorAll('video[data-c-src]'));
    var vio = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        var v = en.target;
        if (en.isIntersecting && en.intersectionRatio >= 0.35) { v.dataset.cVis = '1'; playVideo(v); }
        else if (!en.isIntersecting) { delete v.dataset.cVis; v.pause(); }
      });
    }, { threshold: [0, 0.35] });
    videos.forEach(function (v) { vio.observe(v); });
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) { videos.forEach(function (v) { v.pause(); }); return; }
      // Coming back to the tab: resume only what is actually on screen —
      // the observer does not re-fire on tab visibility.
      videos.forEach(function (v) {
        if (v.dataset.cVis && v.loop) { var p = v.play(); if (p && p.catch) p.catch(function () {}); }
      });
    });
  }

  /* ── Motion layer ───────────────────────────────────────────────────── */
  if (REDUCED || !window.gsap || !window.ScrollTrigger || !window.Lenis) {
    docEl.classList.remove('bj3');
    return;
  }

  var gsap = window.gsap;
  var ScrollTrigger = window.ScrollTrigger;
  gsap.registerPlugin(ScrollTrigger);
  ScrollTrigger.config({ ignoreMobileResize: true });

  // ONE rAF: Lenis raf runs FIRST inside gsap's ticker; render (the world)
  // appends itself last. scrub:true everywhere — Lenis owns the smoothing.
  var lenis = new Lenis({ lerp: 0.1, smoothWheel: true, syncTouch: false });
  lenis.on('scroll', ScrollTrigger.update);
  gsap.ticker.add(function (t) { lenis.raf(t * 1000); }, false, true);
  gsap.ticker.lagSmoothing(0);

  window.__bj3Live = true;           // head failsafe stands down
  window.__bj3Lenis = lenis;         // for anchor routing + test harnesses
  docEl.classList.add('bj3');        // (idempotent — head already added it)

  // Anchor links must go THROUGH Lenis: a native hash jump sets scrollTop
  // once, then Lenis's next raf lerps it straight back to where it was —
  // measured live (scrollTo(0) ended at 14872). Route every same-page
  // anchor through lenis.scrollTo instead.
  document.addEventListener('click', function (e) {
    var a = e.target.closest && e.target.closest('a[href^="#"]');
    if (!a) return;
    var id = a.getAttribute('href');
    if (id.length < 2) return;
    var target = document.querySelector(id);
    if (!target) return;
    e.preventDefault();
    lenis.scrollTo(target, { offset: -72, duration: 1.4 });
    history.pushState(null, '', id);
  });

  // Global velocity signal: raw → damped → clamped, zeroed at rest.
  var vel = { raw: 0, value: 0 };
  lenis.on('scroll', function (e) { vel.raw = e.velocity || 0; });
  gsap.ticker.add(function () {
    var target = Math.max(-1, Math.min(1, vel.raw * 0.014));
    vel.value += (target - vel.value) * 0.09;
    if (Math.abs(vel.value) < 0.002) vel.value = 0;
    vel.raw *= 0.86;
    window.__bj3Vel = vel.value;
  });

  /* ── Reveals — is-in SETS final values; the sweep re-SETS, never hides ─ */
  function reveal(els) {
    gsap.utils.toArray(els).forEach(function (el) {
      ScrollTrigger.create({
        trigger: el, start: 'top 88%', once: true,
        onEnter: function () { el.classList.add('is-in'); }
      });
    });
  }
  reveal('.c-arm');
  reveal('.c-ln__i');
  /* The act rule — one shared transition, fired the same way as every other
     reveal so there is exactly one mechanism on this page to reason about. */
  gsap.utils.toArray('.c-room, .c-collection, .c-book').forEach(function (act) {
    ScrollTrigger.create({
      trigger: act, start: 'top 92%', once: true,
      onEnter: function () { act.classList.add('is-act-in'); }
    });
  });
  // Safety sweep: anything armed but on-screen for 2.5s becomes visible.
  setInterval(function () {
    document.querySelectorAll('html.bj3 .c-arm:not(.is-in), html.bj3 .c-ln__i:not(.is-in)').forEach(function (el) {
      var r = el.getBoundingClientRect();
      if (r.top < innerHeight && r.bottom > 0) el.classList.add('is-in');
    });
  }, 2500);

  /* ── HERO — the pinned film (the "one-shot" blueprint, made robust) ─── */
  (function hero() {
    var hero = document.getElementById('hv3-hero');
    var film = hero && hero.querySelector('.c-hero__film');
    if (!hero || !film) return;

    var SCRUB_OK = !IS_TOUCH && innerWidth >= 900;

    // The film joins the sitewide pause lifecycle (hidden tab, off-screen)
    // whichever mode it runs in — it was the one unmanaged decoder.
    videos.push(film);
    var scrubDead = false;
    var heroProgress = 0;
    var fill = document.querySelector('[data-c-heroprog]');
    var inner = hero.querySelector('.c-hero__inner');

    // Fade + cue-rail are driven from the PIN's own progress — sibling
    // ScrollTriggers on a pinned element get pin-compensated at refresh
    // and drift (verified against the vendored ScrollTrigger source).
    var applyHero = function (p) {
      heroProgress = p;
      if (fill) fill.style.transform = 'scaleY(' + p + ')';
      if (inner) {
        var f = Math.min(1, Math.max(0, (p - 0.55) / 0.45));
        inner.style.opacity = String(1 - f);
        inner.style.transform = 'translateY(' + (-f * 9) + '%)';
      }
    };

    if (SCRUB_OK) {
      // Frame-lock: all-keyframe encode, currentTime driven by scroll.
      film.src = film.dataset.cScrub;
      film.preload = 'auto';
      film.load();
      // Duration is read DIRECTLY each update — a loadedmetadata listener
      // races the memory cache and the scrub silently never moves.
      var lastT = -1;
      ScrollTrigger.create({
        trigger: hero,
        start: 'top top',
        end: '+=190%',
        pin: true,
        scrub: 0.5,
        onUpdate: function (self) {
          applyHero(self.progress);
          if (scrubDead) return;
          var d = film.duration;
          if (!d || isNaN(d) || film.readyState < 1) return;
          var t = Math.min(d - 0.05, self.progress * d);
          if (Math.abs(t - lastT) > 0.0416) { // one 24fps frame — finer
            film.currentTime = t;             // seeks just flood the decoder
            lastT = t;
          }
        }
      });
      // If the scrub encode can't PLAY (offline, blocked, starved), swap to
      // the loop AND retire the scrub — leaving it alive had the seek loop
      // fighting the playing loop at 50Hz.
      setTimeout(function () {
        if (film.readyState < 2) {
          scrubDead = true;
          film.src = film.dataset.cLoop;
          film.loop = true; film.load();
          playVideo(film);
        }
      }, 4000);
    } else {
      // Touch: the loop plays; scroll drives the type, not the frame. The
      // observer gains it too, so it pauses out of view like every film.
      scrubDead = true;
      film.src = film.dataset.cLoop;
      film.loop = true;
      film.load();
      var p0 = film.play();
      if (p0 && p0.catch) p0.catch(function () {});
      if (typeof vio !== 'undefined') vio.observe(film);
      ScrollTrigger.create({
        trigger: hero, start: 'top top', end: '+=120%', pin: true, scrub: true,
        onUpdate: function (self) { applyHero(self.progress); }
      });
    }

    // Entrance — CSS transitions own the reveal (is-in SETS final state;
    // GSAP never animates the same properties — the double-easing trap).
    // One rAF so the armed state paints first and the transition fires.
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        hero.querySelectorAll('.c-arm, .c-ln__i').forEach(function (el) {
          el.classList.add('is-in');
        });
      });
    });

    // (hero fade + cue rail live inside the pin's onUpdate above)
  })();

  /* ── TICKER — velocity-reactive marquee ─────────────────────────────── */
  (function ticker() {
    var track = document.querySelector('[data-c-ticker]');
    if (!track) return;
    var x = 0, half = 0;
    // The true period is first-child-of-copy-2 minus first-child-of-copy-1
    // (scrollWidth/2 is short by half a flex gap and stutters every wrap),
    // and it must be re-measured once webfonts land.
    var measure = function () {
      var kids = track.children;
      var mid = kids[kids.length / 2];
      half = mid ? (mid.offsetLeft - kids[0].offsetLeft) : track.scrollWidth / 2;
    };
    measure();
    addEventListener('resize', measure);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(measure);
    gsap.ticker.add(function (t, dt) {
      if (!half || document.hidden) return;
      var speed = 0.028 + Math.abs(window.__bj3Vel || 0) * 0.35;
      x -= speed * dt;
      if (-x >= half) x += half;
      track.style.transform = 'translate3d(' + x + 'px,0,0)';
    });
  })();

  /* ── Shelf + letters — drift & skew with velocity (native scroll) ───── */
  gsap.utils.toArray('[data-c-shelf], [data-c-letters]').forEach(function (rail) {
    gsap.ticker.add(function () {
      var v = window.__bj3Vel || 0;
      rail.style.transform = 'skewX(' + (-v * 2.2) + 'deg)';
    });
  });

  /* ── Room stage tilt — the object leans as it enters. NEVER the two
     plate stages: the WebGL plate is glued to a rect cached at refresh,
     and a scrub-tweened rect walks out of register with its own plate. ── */
  gsap.utils.toArray('.c-room__stage:not(.c-room__stage--still)').forEach(function (stage) {
    gsap.fromTo(stage,
      { yPercent: 7, rotateZ: 0.001 },
      {
        yPercent: -7, ease: 'none',
        scrollTrigger: { trigger: stage, start: 'top bottom', end: 'bottom top', scrub: true }
      });
  });

  /* ── THE HELD BEAT ──────────────────────────────────────────────────────
     The page ran top to bottom at one constant speed — no pause, no moment
     that stops you. The price room now PINS: the scroll keeps going, the
     room does not, and while it is held the number grows and the macro
     settles. That tempo change is the whole difference between a document
     and a film. Desktop only — pinning a full-height section on a phone
     costs more than it gives, and the project already ships an unpinned
     mobile variant of every act. */
  if (!IS_TOUCH && innerWidth >= 900) {
    var held = document.querySelector('#iced');
    var heldPrice = held && held.querySelector('[data-c-colossal]');
    var heldMedia = held && held.querySelector('.c-room__stage img');
    if (held && heldPrice && heldMedia) {
      gsap.timeline({
        scrollTrigger: {
          trigger: held, start: 'top top', end: '+=90%',
          pin: true, scrub: true, anticipatePin: 1
        }
      })
        .fromTo(heldPrice, { scale: 0.84, transformOrigin: '0% 50%' },
                           { scale: 1, ease: 'none' }, 0)
        .fromTo(heldMedia, { scale: 1.14 }, { scale: 1, ease: 'none' }, 0);
    }
  }

  /* ── Room media depth — the photograph sits BEHIND the copy ─────────────
     The stage already leans; the film itself was flat inside it, so a room
     read as two columns side by side rather than a lit object in a space.
     A slow counter-scale gives the media its own plane. Scoped to the same
     :not(--still) exclusion as the tilt above — a plate stage's rect is
     cached at refresh and must never be tweened. */
  gsap.utils.toArray('.c-room__stage:not(.c-room__stage--still) .c-room__film').forEach(function (m) {
    gsap.fromTo(m, { scale: 1.07 }, {
      scale: 1, ease: 'none',
      scrollTrigger: { trigger: m, start: 'top bottom', end: 'center center', scrub: true }
    });
  });

  /* ── The shelf is a THING YOU HANDLE, not a scrollbar ───────────────────
     It was a native horizontal scroller with a velocity skew: on a mouse
     there was nothing to grab and no reason to touch it, so the last third
     of the page went inert. Three additions, all pointer-gated:
       · drag with flick inertia (fine pointers only — a thumb already has
         native momentum, and hijacking touch here would fight the page)
       · a scroll-linked strafe so the case moves as you come down it,
         which STANDS DOWN permanently the moment the visitor takes hold
       · per-piece pointer tilt via quickTo
     Snap is off on fine pointers (CSS) because mandatory snap fights every
     programmatic scroll; touch keeps it. */
  gsap.utils.toArray('[data-c-shelf]').forEach(function (rail) {
    var FINE = matchMedia('(pointer: fine)').matches;
    if (!FINE) return;

    var taken = false;                 // has the visitor taken hold?
    var down = false, startX = 0, startLeft = 0, lastX = 0, vx = 0, lastT = 0;
    var moved = 0;
    var maxLeft = function () { return rail.scrollWidth - rail.clientWidth; };

    rail.addEventListener('pointerdown', function (e) {
      if (e.button !== 0) return;
      down = true; moved = 0; vx = 0;
      startX = lastX = e.clientX;
      startLeft = rail.scrollLeft;
      lastT = performance.now();
      gsap.killTweensOf(rail);
      // Capture is a nicety (it keeps the drag alive past the rail's edge),
      // never a requirement — and it throws outright if the pointer id is
      // not currently active. An exception here would kill the whole
      // pointerdown handler and the rail would simply stop dragging.
      try { rail.setPointerCapture(e.pointerId); } catch (err) {}
    });

    rail.addEventListener('pointermove', function (e) {
      if (!down) return;
      var dx = e.clientX - startX;
      moved = Math.max(moved, Math.abs(dx));
      // Only claim the gesture once it is clearly a drag — under 4px it is
      // still a click on a product, and stealing it would break every link.
      if (moved > 4) {
        taken = true;
        rail.classList.add('is-dragging');
        rail.scrollLeft = startLeft - dx;
        var now = performance.now(), dt = now - lastT;
        if (dt > 0) vx = (e.clientX - lastX) / dt;
        lastX = e.clientX; lastT = now;
      }
    });

    function release(e) {
      if (!down) return;
      down = false;
      try { rail.releasePointerCapture(e.pointerId); } catch (err) {}
      // pointer-events came back on the anchors, but not before this frame,
      // or the release itself fires the link the drag just finished on.
      requestAnimationFrame(function () { rail.classList.remove('is-dragging'); });
      if (moved <= 4) return;
      var throwTo = rail.scrollLeft - vx * 260;
      gsap.to(rail, {
        scrollLeft: Math.max(0, Math.min(maxLeft(), throwTo)),
        duration: 0.9, ease: 'power3.out', overwrite: true
      });
    }
    rail.addEventListener('pointerup', release);
    rail.addEventListener('pointercancel', release);
    rail.addEventListener('click', function (e) {
      if (moved > 4) { e.preventDefault(); e.stopPropagation(); }
    }, true);

    // The strafe. Yields the moment the visitor drags — a rail that fights
    // the hand is worse than a rail that never moved.
    ScrollTrigger.create({
      trigger: rail, start: 'top bottom', end: 'bottom top', scrub: true,
      onUpdate: function (self) {
        if (taken || down) return;
        var m = maxLeft();
        if (m <= 0) return;
        rail.scrollLeft = Math.max(0, Math.min(m, self.progress * m * 0.85));
      }
    });

    // Per-piece tilt. quickTo, so the pointer never queues tweens.
    gsap.utils.toArray(rail.querySelectorAll('.c-shelf__item')).forEach(function (item) {
      var media = item.querySelector('.c-shelf__media');
      if (!media) return;
      var rx = gsap.quickTo(media, 'rotationX', { duration: 0.5, ease: 'power3' });
      var ry = gsap.quickTo(media, 'rotationY', { duration: 0.5, ease: 'power3' });
      item.addEventListener('pointermove', function (e) {
        if (down) return;
        var r = item.getBoundingClientRect();
        rx(((e.clientY - r.top) / r.height - 0.5) * -7);
        ry(((e.clientX - r.left) / r.width - 0.5) * 9);
      });
      item.addEventListener('pointerleave', function () { rx(0); ry(0); });
    });
  });

  /* ── Magnetic gold slabs ────────────────────────────────────────────────
     The one filled CTA leans toward the cursor inside its own bounds. The
     stylesheet drops `transform` from the transition on .is-magnetic so
     GSAP owns that property alone — two easings on one property is the
     double-easing trap documented in this project. The -3px baseline is the
     old CSS hover lift, folded into the magnet so it is not lost. */
  if (matchMedia('(pointer: fine)').matches) {
    gsap.utils.toArray('.c-btn, .c-chip, .c-ghost').forEach(function (btn) {
      btn.classList.add('is-magnetic');
      var x = gsap.quickTo(btn, 'x', { duration: 0.4, ease: 'power3' });
      var y = gsap.quickTo(btn, 'y', { duration: 0.4, ease: 'power3' });
      btn.addEventListener('pointermove', function (e) {
        var r = btn.getBoundingClientRect();
        x((e.clientX - (r.left + r.width / 2)) * 0.28);
        y((e.clientY - (r.top + r.height / 2)) * 0.34 - 3);
      });
      btn.addEventListener('pointerleave', function () { x(0); y(0); });
    });
  }

  /* ── Fonts settle → measurements are real (documented trap) ─────────── */
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(function () { ScrollTrigger.refresh(); });
  }
  // Lazy images grow the page — debounce a refresh per load (v2 lesson).
  var refreshT = 0;
  document.querySelectorAll('img[loading="lazy"]').forEach(function (img) {
    img.addEventListener('load', function () {
      clearTimeout(refreshT);
      refreshT = setTimeout(function () { ScrollTrigger.refresh(); }, 220);
    }, { once: true });
  });

  /* ── THE WORLD — tiered gate, then dynamic import ───────────────────── */
  (function world() {
    if (navigator.connection && navigator.connection.saveData) return;
    if (navigator.deviceMemory && navigator.deviceMemory < 2) return;
    if (innerWidth < 360) return;
    var probe = document.createElement('canvas');
    var gl = probe.getContext('webgl2') || probe.getContext('webgl');
    if (!gl) return;
    var tier = IS_TOUCH ? 1 : (innerWidth >= 1600 ? 3 : 2);
    // Dynamic import inside a CLASSIC script resolves against THIS file's
    // URL (/js/bj-v3.js), so the sibling module is './bj-world.js' —
    // './js/bj-world.js' would resolve to /js/js/ and 404.
    (new Function('u', 'return import(u)'))(new URL((window.BJ_ASSETS||'./')+'bj-world.js', document.baseURI).href).then(function (m) {
      return m.init({
        mount: document.getElementById('bj3-stage'),
        tier: tier,
        lenis: lenis,
        getVelocity: function () { return window.__bj3Vel || 0; }
      });
    }).then(function (ok) {
      if (!ok || !window.BJWorld) return;
      // World mode only on PROOF: a corridor texture must actually load
      // before the DOM descent strip is traded for the WebGL flight. A
      // constructed-but-broken renderer now costs the page nothing.
      return window.BJWorld.ready.then(function (drew) {
        if (!drew) return;
        docEl.classList.add('bj3w');
        // The descent grew to 440vh — every cached trigger is now wrong.
        requestAnimationFrame(function () { ScrollTrigger.refresh(); });
      });
    }).catch(function () { /* the page stands on its own */ });
  })();

})();
